/**
 * `/waitlist` — public waitlist form.
 *
 * Posts to `POST /api/v0/waitlist`. On success, navigates to
 * `/waitlist/confirmed` carrying the queue-position payload in router state.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthShell } from '../components/layout/AuthShell';
import { applyToWaitlist } from '../api/waitlist';
import { ApiCallError } from '../api/client';

export function WaitlistPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (submitting) return;
    setSubmitting(true);
    const form = new FormData(e.currentTarget);
    try {
      const result = await applyToWaitlist({
        email: String(form.get('email') ?? ''),
        name: String(form.get('name') ?? ''),
        school: String(form.get('school') ?? ''),
        event: String(form.get('event') ?? ''),
        circuit: String(form.get('circuit') ?? ''),
        whyYou: String(form.get('whyYou') ?? ''),
        acknowledgedBetaTerms: true,
      });
      navigate('/waitlist/confirmed', { state: { result } });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      topRight={
        <>
          <Link to="/invite">Have an invite code?</Link>
          <Link to="/sign-in">Sign in</Link>
        </>
      }
    >
      <div className="auth-card wide">
        <div className="auth-eyebrow">
          <span className="num">01</span> · Closed beta
        </div>
        <h1 className="auth-title">
          Join the <em>waitlist</em>.
        </h1>
        <p className="auth-sub">
          Eloquor is currently in closed beta. The fastest way to get access is to ask a friend who's already using it to invite you. Otherwise, you can join the waitlist and you'll be emailed if a spot becomes available.
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="name">
              Full name
            </label>
            <input
              className="input"
              id="name"
              name="name"
              type="text"
              placeholder="Raymond Xu"
              required
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              className="input"
              id="email"
              name="email"
              type="email"
              placeholder="you@school.edu"
              required
            />
          </div>
          <div className="field-row">
            <div className="field">
              <label className="label" htmlFor="school">
                School / program
              </label>
              <input
                className="input"
                id="school"
                name="school"
                type="text"
                placeholder="Delbarton"
                required
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="event">
                Primary event
              </label>
              <select className="select" id="event" name="event" required defaultValue="">
                <option value="" disabled>
                  Select…
                </option>
                <option>Policy (CX)</option>
                <option>Lincoln–Douglas</option>
                <option>Public Forum</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="circuit">
              Circuit
            </label>
            <select className="select" id="circuit" name="circuit" required defaultValue="">
              <option value="" disabled>
                Select…
              </option>
              <option>National (TOC)</option>
              <option>State / Regional (local)</option>
              <option>NSDA only</option>
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="whyYou">
              Why you?
            </label>
            <textarea
              className="input"
              id="whyYou"
              name="whyYou"
              rows={5}
              maxLength={1000}
              placeholder="A few sentences on your debate experience and what you'd use Eloquor for."
              required
              style={{ resize: 'vertical', minHeight: 96, fontFamily: 'inherit' }}
            />
          </div>

          <div className="check-row">
            <input
              type="checkbox"
              id="terms"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
            />
            <label htmlFor="terms">
              I understand Eloquor is in closed beta and that I will be contacted via email if I am selected as a beta tester.
            </label>
          </div>

          {error && (
            <div className="helper err">
              <span className="dot" />
              <span>{error}</span>
            </div>
          )}

          <button className="btn-primary ember" type="submit" disabled={submitting || !consent}>
            {submitting ? 'Sending…' : 'Request access'} <span aria-hidden>→</span>
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
